import React from "react";
import { FiBox, FiStar, FiZap, FiLayers, FiGlobe } from "react-icons/fi"; 
import { useLandingPageContext } from "pages/Context/LandinPageContext";
import { IPeriodicTime } from "core/enums/plans.enum";
import { PlanWithDiscount } from "..";
import IconPresent from "components/Icons/IconPresent";
import LField from "components/Inputs/LField";

interface PlanCardProps {
  plan: PlanWithDiscount;
  benefitOrder: string[];
}

const PlanCard = ({ plan, benefitOrder }:PlanCardProps) => {
  const { selectedPlanContext, setSelectedPlanContext, quantityOnDemandContext, setQuantityOnDemandContext } = useLandingPageContext();
  const periodDetails = {
    [IPeriodicTime.FREE]: { label: "processo", months: 0 },
    [IPeriodicTime.ON_DEMAND]: { label: "processo", months: 0 },
    [IPeriodicTime.MONTHLY]: { label: "mês", months: 1 },
    [IPeriodicTime.BIMONTHLY]: { label: "bimestre", months: 2 },
    [IPeriodicTime.QUARTERLY]: { label: "trimestre", months: 3 },
    [IPeriodicTime.SEMESTERLY]: { label: "semestre", months: 6 },
    [IPeriodicTime.YEARLY]: { label: "ano", months: 12 },
    [IPeriodicTime.SPECIFIC]: { label: "período", months: 0 },
  }[plan.period.type];
  const equivalentMonthlyAmount = periodDetails.months > 1
    ? plan.allAmount / periodDetails.months
    : null;
  const maxInstallments = Math.max(1, Number(plan.period.installmentCount) || 1);
  const formatMoney = (value: number) => value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  const paymentTotal = plan.allAmount * (plan.period.type === IPeriodicTime.ON_DEMAND && plan.maxOnDemandQuantity !== 1 ? quantityOnDemandContext : 1);
  const paymentDescription = plan.period.type === IPeriodicTime.MONTHLY
    ? "Pagamento mensal"
    : maxInstallments === 1
      ? `À vista: ${formatMoney(paymentTotal)}`
      : `Até ${maxInstallments}× de ${formatMoney(paymentTotal / maxInstallments)}`;
  const benefits = [...(plan.benefit ?? [])].sort((a, b) => benefitOrder.indexOf(a) - benefitOrder.indexOf(b));
  const isPlanOnDemand= plan.period.type === IPeriodicTime.ON_DEMAND;
  const lockQuantity = isPlanOnDemand && plan.maxOnDemandQuantity === 1;

  if(plan.period.type === IPeriodicTime.ON_DEMAND) {
    plan = {...plan, allAmountCurrency: `${quantityOnDemandContext}x R$ ${(plan.allAmount*quantityOnDemandContext).toFixed(2).replace(".", ",")}`};
  }

  const iconMap: { [key: string]: any } = {
    [IPeriodicTime.FREE]: IconPresent,
    [IPeriodicTime.ON_DEMAND]: FiBox,
    [IPeriodicTime.MONTHLY]: FiStar,
    [IPeriodicTime.QUARTERLY]: FiZap,
    [IPeriodicTime.SEMESTERLY]: FiLayers,
    [IPeriodicTime.YEARLY]: FiGlobe,
  };

  const IconComponent = iconMap[plan.period.type];

  return (
    <div
      className={`plan-comparison-card p-3 rounded-lg border transition-all duration-300 hover:shadow-2xl ${
        selectedPlanContext?._id === plan?._id
          ? "bg-blue-600 text-white hover:bg-blue-500"
          : "bg-white text-gray-800 hover:bg-blue-100"
      }`}
      style={{ minWidth: "217px"}}
    >
      <div className="plan-card-contents">
        <div className="plan-card-heading flex items-center gap-2">
          {IconComponent && (
            <div className="bg-white p-2 rounded-full drop-shadow transition-colors duration-300 hover:bg-gray-200">
              <IconComponent
                className={"text-2xl text-blue-600 hover:text-blue-400"}
              />
            </div>
          )}
          <h3
            className={`text-xl font-semibold mb-0 ${
              selectedPlanContext?._id === plan._id ? "text-white" : "text-blue-600"
            }`}
          >
            {plan.name}
          </h3>
        </div>
        <div className="plan-card-price-details">
        <p className="plan-card-price text-2xl font-medium">{formatMoney(plan.allAmount)}</p>
        <p className="text-sm mb-3">por {periodDetails.label}</p>
        {equivalentMonthlyAmount !== null && (
          <p className="font-inter font-normal text-sm">Equivalente a {formatMoney(equivalentMonthlyAmount)}/mês</p>
        )}
        {isPlanOnDemand && !lockQuantity && <p className="font-inter font-normal text-sm">Total {plan.allAmountCurrency}</p>}
        {plan.allAmount > 0 && (
          <div className="font-inter font-normal text-sm mt-2">
            <p>{paymentDescription}</p>
          </div>
        )}
        {plan.discount !== "0" && <p className={`font-inter text-sm ${selectedPlanContext?._id === plan._id ? "text-white" : "text-blue-600"}`}>{plan.discount}</p>}
        </div>
      </div>

      <div className="plan-card-actions">
        <div className="plan-card-quantity">
        { isPlanOnDemand && !lockQuantity &&
          <div className="flex items-center justify-start gap-3">
            <button
              type="button"
              onClick={() => setQuantityOnDemandContext((prev) => Math.max(1, prev - 1))}
              className={`px-3 rounded-full text-xl font-bold text-blue-600 bg-transparent ${
                selectedPlanContext?._id === plan._id ? "bg-blue-600 text-white hover:bg-blue-500" : "bg-white text-blue-600 hover:bg-gray-200"
              }`}
            >
              −
            </button>
            <LField
              type="number"
              min={1}
              value={quantityOnDemandContext}
              onChange={(e) => {
                const value = Math.max(1, parseInt(e.target.value) || 1);
                setQuantityOnDemandContext(value);
              }}
              className="text-center rounded-md py-1 my-0"
              classNameInput="text-center  no-spinner"
            />
            <button
              type="button"
              onClick={() => setQuantityOnDemandContext((prev) => prev + 1)}
              className={`px-3 py-1 rounded-full text-xl font-bold text-blue-600 bg-transparent ${
                selectedPlanContext?._id === plan._id ? "bg-blue-600 text-white hover:bg-blue-500" : "bg-white text-blue-600 hover:bg-gray-200"
              }`}
            >
              +
            </button>
          </div>
        }
        </div>
        <button
          className={`text-sm sm:text-base px-4 py-2 rounded-full font-medium transition-colors duration-300 ${
            selectedPlanContext?._id === plan._id ? "bg-white text-blue-600 hover:bg-gray-200" : "bg-blue-600 text-white hover:bg-blue-500"
          }`}
          onClick={() => {
            if (lockQuantity) setQuantityOnDemandContext(1);
            setSelectedPlanContext(plan);
            // BUG-050: rolar suave até o formulário e dar foco no primeiro campo.
            setTimeout(() => {
              const form = document.getElementById("register-form");
              if (form) {
                form.scrollIntoView({ behavior: "smooth", block: "start" });
                const firstInput = form.querySelector<HTMLInputElement>("input:not([type=hidden]), select, textarea");
                firstInput?.focus({ preventScroll: true });
              }
            }, 0);
          }}
        >
          Selecionar
        </button>
      </div>
        <ul className="plan-card-benefits d-flex flex-column justify-content-start p-0">
          {benefits.map((item, index) => {
            const isFirstBenefit = index === 0;
            return (
              <li key={index} className={`flex items-start text-sm sm:text-base ${isFirstBenefit? "fw-semibold": ""}`}>
                <span
                  className={`mr-2 ${
                    selectedPlanContext?._id === plan._id ? "text-white" : "text-blue-600"
                  }`}
                >
                  ✓
                </span>
                <p className="m-0 text-start">{item}</p>
              </li>
            );
          })

          }
        </ul>
    </div>
  );
};

export default PlanCard;

